import { FocusMonitor } from '@angular/cdk/a11y';
import { OverlayModule } from '@angular/cdk/overlay';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { NgClass, NgIf } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DoCheck,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  Optional,
  Output,
  Self,
  ViewChild,
  booleanAttribute,
  forwardRef,
} from '@angular/core';
import { ControlValueAccessor, FormsModule, NgControl } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { parsePhoneNumber } from 'libphonenumber-js/mobile';
import { isNil, noop } from 'lodash-es';
import { NgxMaskDirective } from 'ngx-mask';
import { map } from 'rxjs';

import { ContainerComponent } from '@aw/shared/components/container';
import {
  CanUpdateErrorState,
  ErrorStateMatcher,
  FormFieldControl,
  mixinErrorState,
} from '@aw/shared/components/form-field';
import { IconModule } from '@aw/shared/components/icon';
import { RippleDirective } from '@aw/shared/directives';
import { IdPipe } from '@aw/shared/pipes';
import { getElementWidth, isEmptyString } from '@aw/shared/utilities';

import { CountryPrefixSelectComponent } from './components/country-prefix-select/country-prefix-select.component';
import {
  coerceCountryCallingCode,
  isCountryCallingCode,
} from './utils/phone-input.utils';

const _InputBase = mixinErrorState(class {});

type onChangeFn = (value: string) => void;

// TODO: If you paste in a complete phone number split off the prefix, select it and fill in the national number
// TODO: If closing the prefix select with a new selection and no phone number entered, automatically focus the phone input part

@UntilDestroy()
@Component({
  standalone: true,
  selector: 'aw-phone-input',
  templateUrl: 'phone-input.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgIf,
    NgClass,
    FormsModule,
    OverlayModule,
    ScrollingModule,
    IconModule,
    IdPipe,
    RippleDirective,
    CountryPrefixSelectComponent,
    ContainerComponent,
    NgxMaskDirective,
  ],
  providers: [
    {
      provide: FormFieldControl,
      useExisting: forwardRef(() => PhoneInputComponent),
    },
  ],
})
export class PhoneInputComponent
  extends _InputBase
  implements
    DoCheck,
    AfterViewInit,
    FormFieldControl<string>,
    ControlValueAccessor,
    CanUpdateErrorState
{
  static nextId = 0;

  @HostBinding('attr.id')
  readonly id = `aw-phone-input-${PhoneInputComponent.nextId++}`;

  @Input()
  set value(_value: string | null) {
    this._value = _value;

    this.onValueChange(_value);
    this.stateChanges.next();
  }

  get value(): string | null {
    return this._value;
  }

  @Input({ transform: booleanAttribute }) disabled = false;

  @Input() defaultPrefix = '+971';

  @Input() label?: string;

  @Input() placeholder = 'Mobile number';

  @Output() readonly valueChange = new EventEmitter<string>();

  @Output() readonly prefixValueChange = new EventEmitter<string>();

  get fieldWidth(): number {
    return getElementWidth(this.elementRef.nativeElement);
  }

  get prefix(): string {
    return this.countryCallingCode || this.defaultPrefix;
  }

  nationalNumber?: string;

  onTouched?: () => void;

  onChange?: onChangeFn;

  isFocused = false;

  constructor(
    private readonly elementRef: ElementRef,
    @Optional() @Self() readonly ngControl: NgControl,
    private readonly focusMonitor: FocusMonitor,
    private readonly changeDetector: ChangeDetectorRef,
    readonly defaultErrorStateMatcher: ErrorStateMatcher,
  ) {
    super();

    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  @ViewChild('input', { static: true })
  private readonly input?: ElementRef<HTMLInputElement>;

  private countryCallingCode?: string;

  private _value: string | null = null;

  private get completePhoneNumber(): string {
    return `${this.prefix}${this.nationalNumber || ''}`;
  }

  ngDoCheck(): void {
    if (this.ngControl) {
      this.updateErrorState();
    }
  }

  ngAfterViewInit(): void {
    if (this.input) {
      this.initInputFocusMonitor(this.input.nativeElement);
    }
  }

  onPrefixChange(countryCallingCode: string): void {
    this.countryCallingCode = countryCallingCode;

    if (
      this.input &&
      (isNil(this.nationalNumber) || isEmptyString(this.nationalNumber))
    ) {
      this.input.nativeElement.focus();
    }

    this.emitValue();
    this.prefixValueChange.emit(this.countryCallingCode);
  }

  onInputChange(value: string): void {
    this.nationalNumber = value;

    this.emitValue();
  }

  onFocus(): void {
    this.isFocused = true;
  }

  onBlur(): void {
    this.isFocused = false;

    if (this.onTouched) {
      this.onTouched();
    }
  }

  writeValue(value: string | null): void {
    this.value = value;
    this.changeDetector.markForCheck();
  }

  setDisabledState(disabled: boolean): void {
    this.disabled = disabled;
    this.stateChanges.next();
    this.changeDetector.detectChanges();
  }

  registerOnChange(fn: onChangeFn): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  private onValueChange(value: string | null): void {
    if (isNil(value) || isEmptyString(value)) {
      this.countryCallingCode = undefined;
      this.nationalNumber = undefined;
    } else {
      try {
        const { countryCallingCode, nationalNumber } = parsePhoneNumber(value);

        this.countryCallingCode = coerceCountryCallingCode(countryCallingCode);
        this.nationalNumber = nationalNumber;
      } catch (error) {
        noop(error);

        if (isCountryCallingCode(value)) {
          this.countryCallingCode = value;
        } else {
          this.countryCallingCode = undefined;
        }

        this.nationalNumber = undefined;
      }
    }
  }

  private initInputFocusMonitor(input: HTMLInputElement): void {
    this.focusMonitor
      .monitor(input)
      .pipe(
        map((origin) => !isNil(origin)),
        untilDestroyed(this),
      )
      .subscribe((focused) => {
        this.isFocused = focused;
        this.changeDetector.markForCheck();
      });
  }

  private emitValue(): void {
    const { completePhoneNumber } = this;

    if (this.onChange) {
      this.onChange(completePhoneNumber);
    } else {
      this.valueChange.emit(completePhoneNumber);
    }
    this.prefixValueChange.emit(this.prefix);
    this.changeDetector.markForCheck();
  }
}
